// Click-to-sort + quick-filter helpers for the plain admin tables.
// Keeps logic out of the list components so each list just declares its
// columns and the search fields.

import React, { useMemo, useState } from 'react';

// Case-insensitive locale-aware comparator that handles missing values.
function cmp(a, b) {
  const A = a == null ? '' : String(a);
  const B = b == null ? '' : String(b);
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return A.localeCompare(B, undefined, { sensitivity: 'base', numeric: true });
}

// Resolve a column value from a row. Either a string key (`'name'`),
// or a function (`row => derived`).
function valueOf(row, accessor) {
  if (typeof accessor === 'function') return accessor(row);
  return row?.[accessor];
}

// useTableSort(rows, { defaultKey, defaultDir, accessors, filterFields, filterText })
// Returns: { sortedRows, sortKey, sortDir, toggleSort }
export function useTableSort(rows, {
  defaultKey,
  defaultDir = 'asc',
  accessors = {},
  filterFields = [],
  filterText = '',
} = {}) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const filtered = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(row => filterFields.some(f => {
      const v = valueOf(row, accessors[f] ?? f);
      return v != null && String(v).toLowerCase().includes(q);
    }));
  }, [rows, filterText, filterFields, accessors]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filtered;
    const accessor = accessors[sortKey] ?? sortKey;
    const copy = [...filtered];
    copy.sort((a, b) => cmp(valueOf(a, accessor), valueOf(b, accessor)));
    if (sortDir === 'desc') copy.reverse();
    return copy;
  }, [filtered, sortKey, sortDir, accessors]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return { sortedRows, sortKey, sortDir, toggleSort };
}

// Clickable header cell with a sort-direction indicator.
export function SortableTh({ label, sortKeyName, sortKey, sortDir, onClick }) {
  const active = sortKey === sortKeyName;
  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      onClick={() => onClick(sortKeyName)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}{arrow}
    </th>
  );
}
